import { useEffect, useState } from "react";
import { Loader2, Building2, MapPin, FileText, Search, Link2, RefreshCw, ExternalLink, ShieldCheck, Unlink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { getAsicAbrProfile, linkAsicAbr, unlinkAsicAbr, searchAsicAbr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const ASIC_STATUS = { REGD: ["Registered", "bg-emerald-50 text-emerald-700 border-emerald-300"], DRGD: ["Deregistered", "bg-red-50 text-red-700 border-red-300"], EXAD: ["External administration", "bg-amber-50 text-amber-800 border-amber-300"], SOFF: ["Strike-off action", "bg-amber-50 text-amber-800 border-amber-300"], NOAC: ["Not active", "bg-slate-100 text-slate-600 border-slate-300"] };
const ASIC_TYPE = { APTY: "Australian proprietary company", APUB: "Australian public company", ASSN: "Association", FNOS: "Foreign company", RACN: "Registered Australian body", TRST: "Trust" };
const fmtAbn = (a) => (a ? a.replace(/^(\d{2})(\d{3})(\d{3})(\d{3})$/, "$1 $2 $3 $4") : "—");
const fmtAcn = (a) => (a ? a.replace(/^(\d{3})(\d{3})(\d{3})$/, "$1 $2 $3") : "—");

function InfoCard({ icon, label, children, testid }) {
  return (
    <div className="border rounded-lg bg-white p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-1.5">{icon}{label}</p>
      <div className="text-sm space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return <p className="flex justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v ?? "—"}</span></p>;
}

function Candidates({ caseId, list, onLinked, user }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(list || []);
  const [busy, setBusy] = useState(null);
  const search = async () => {
    if (q.trim().length < 2) return;
    setBusy("search");
    try { setRows((await searchAsicAbr(caseId, q.trim())).candidates || []); } catch { toast.error("ABR search failed"); } finally { setBusy(null); }
  };
  const link = async (c) => {
    setBusy(c.abn);
    try { onLinked(await linkAsicAbr(caseId, { abn: c.abn, actioning_user: user?.email || "" })); toast.success(`Linked ABN ${fmtAbn(c.abn)}`); }
    catch (e) { toast.error(errorDetailMessage(e, "Link failed")); } finally { setBusy(null); }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search the ABR by business name or ABN…" className="h-8 text-sm" data-testid="asic-search-input" />
        <Button size="sm" variant="outline" className="h-8" onClick={search} disabled={busy === "search"} data-testid="asic-search-btn">{busy === "search" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}</Button>
      </div>
      <div className="border rounded divide-y max-h-64 overflow-y-auto">
        {rows.length === 0 && <p className="p-3 text-xs text-muted-foreground">No ABR matches — try a shorter name or the ABN.</p>}
        {rows.map((c) => (
          <button key={c.abn + c.business_name} onClick={() => link(c)} disabled={!!busy} className="w-full text-left p-2 text-sm hover:bg-emerald-50 flex items-center justify-between gap-2" data-testid={`asic-candidate-${c.abn}`}>
            <span className="min-w-0"><span className="font-medium">{c.business_name}</span> <span className="text-muted-foreground text-xs">· ABN {fmtAbn(c.abn)} · {c.entity_type} · {c.state} {c.postcode}</span></span>
            {busy === c.abn ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Badge variant="outline" className="text-[10px]"><Link2 className="w-3 h-3 mr-1" />Link</Badge>}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AsicAbrTab({ caseRow }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async (refresh = false) => {
    setLoading(true);
    try { setData(await getAsicAbrProfile(caseRow.case_id, refresh)); }
    catch (e) { toast.error(errorDetailMessage(e, "Could not load ASIC / ABR profile")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [caseRow?.case_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !data) return <div className="p-8 text-center text-sm text-muted-foreground" data-testid="asic-loading"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Looking up the ABR…</div>;
  if (!data) return null;

  if (!data.linked) return (
    <div className="border rounded-lg bg-white p-4 space-y-3" data-testid="asic-unlinked">
      <p className="text-sm font-semibold flex items-center gap-2"><Building2 className="w-4 h-4" />ASIC / ABR company profile</p>
      <p className="text-xs text-muted-foreground">No single ABR match for <b>{data.customer_name}</b>{(data.candidates || []).length > 1 ? " — several entities share this name, so check the state/postcode against the application before linking" : ""}. Pick the right entity below (or search) to link its ABN to this case.</p>
      <Candidates caseId={caseRow.case_id} list={data.candidates} user={user} onLinked={setData} />
    </div>
  );

  const abr = data.abr || {}, asic = data.asic || {};
  const [stLabel, stCls] = ASIC_STATUS[asic.status] || [asic.status || "—", "bg-slate-100 text-slate-600 border-slate-300"];
  return (
    <div className="space-y-3" data-testid="asic-profile">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-base font-semibold flex items-center gap-2"><Building2 className="w-4 h-4" />{abr.display_name || abr.business_name || asic.company_name}
            {abr.is_sole_trader && <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-300" data-testid="asic-sole-trader-badge">Sole trader</Badge>}</p>
          {abr.is_sole_trader && <p className="text-xs text-muted-foreground" data-testid="asic-sole-trader-names">Individual <b>{abr.legal_name}</b>{abr.business_names?.length ? <> trading as <b>{abr.business_names.join(", ")}</b></> : " — no registered business name"}</p>}
          {!abr.is_sole_trader && abr.business_names?.length > 0 && <p className="text-xs text-muted-foreground">Business names: {abr.business_names.join(", ")}</p>}
          <p className="text-xs text-muted-foreground">ABN <span className="font-mono">{fmtAbn(data.abn)}</span>{data.acn && <> · ACN <span className="font-mono">{fmtAcn(data.acn)}</span></>} · {data.linked_source ? `from the application's ${data.linked_source}` : data.auto ? "matched by exact name on the ABR" : `linked by ${data.linked_by || "team"}`}</p>
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => load(true)} disabled={loading} data-testid="asic-refresh"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh</Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs text-red-700" onClick={async () => { await unlinkAsicAbr(caseRow.case_id); toast.success("Unlinked"); load(true); }} data-testid="asic-unlink"><Unlink className="w-3.5 h-3.5 mr-1" />Unlink</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <InfoCard icon={<ShieldCheck className="w-3.5 h-3.5" />} label="ABR — Australian Business Register" testid="asic-card-abr">
          <Row k="ABN status" v={<Badge variant="outline" className={abr.status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-red-50 text-red-700 border-red-300"}>{abr.status || "—"}</Badge>} />
          <Row k="Entity type" v={abr.entity_type} />
          <Row k="GST registered" v={abr.gst_registered == null ? "—" : abr.gst_registered ? "Yes" : "No"} />
          <Row k="Sole trader" v={abr.is_sole_trader ? "Yes" : "No"} />
        </InfoCard>
        <InfoCard icon={<FileText className="w-3.5 h-3.5" />} label="ASIC — company register" testid="asic-card-asic">
          {data.acn ? (<>
            <Row k="Company status" v={<Badge variant="outline" className={stCls}>{stLabel}</Badge>} />
            <Row k="Company type" v={ASIC_TYPE[asic.company_type] || asic.company_type} />
            <Row k="Registered" v={asic.registration_date || "—"} />
            <Row k="ACN" v={<span className="font-mono">{fmtAcn(data.acn)}</span>} />
          </>) : (
            <p className="text-xs text-muted-foreground" data-testid="asic-no-acn">{abr.is_sole_trader ? "Sole traders are not registered with ASIC — there is no ACN or company record. Identity is verified via Sumsub KYC." : "No ACN on the ABR record — this entity (trust / partnership / other) is not an ASIC-registered company."}</p>
          )}
        </InfoCard>
        <InfoCard icon={<MapPin className="w-3.5 h-3.5" />} label="Location & links" testid="asic-card-links">
          <Row k="State / postcode" v={[abr.state, abr.postcode].filter(Boolean).join(" ") || "—"} />
          {asic.registered_address && <Row k="Registered address" v={asic.registered_address} />}
          <p className="pt-1 flex flex-col gap-1 text-xs">
            <a className="text-emerald-700 hover:underline inline-flex items-center gap-1" target="_blank" rel="noreferrer" href={`https://abr.business.gov.au/ABN/View?abn=${data.abn}`} data-testid="asic-link-abr"><ExternalLink className="w-3 h-3" />ABN Lookup (abr.business.gov.au)</a>
            {data.acn && <a className="text-emerald-700 hover:underline inline-flex items-center gap-1" target="_blank" rel="noreferrer" href={`https://connectonline.asic.gov.au/RegistrySearch/faces/landing/SearchRegisters.jspx?searchText=${data.acn}`} data-testid="asic-link-asic"><ExternalLink className="w-3 h-3" />ASIC Connect (directors, charges, documents)</a>}
          </p>
        </InfoCard>
      </div>
      <p className="text-[11px] text-muted-foreground">Directors, registered office and charges are not in the free ASIC dataset — open ASIC Connect for a current extract. Data refreshed {data.fetched_at ? new Date(data.fetched_at).toLocaleString("en-AU", { timeZone: "Australia/Sydney", dateStyle: "medium", timeStyle: "short" }) : "—"} Sydney; cached 7 days.</p>
    </div>
  );
}
